<template>
  <router-link
    v-if="!menusCompact"
    class="bug-report"
    :to="target"
  >
    <span class="bug-art" aria-hidden="true">
      <img :src="artUrl" alt="">
    </span>
    <span class="bug-label">
      <span>{{ t('feedback.reportBug') }}</span>
    </span>
  </router-link>
  <button
    v-else
    type="button"
    class="bug-report"
    :class="{ collapsed: !open }"
    :aria-expanded="open"
    :aria-label="t('feedback.reportBug')"
    @click="onTap"
  >
    <span class="bug-art" aria-hidden="true">
      <img :src="artUrl" alt="">
    </span>
    <span class="bug-label" aria-hidden="true">
      <span>{{ t('feedback.reportBug') }}</span>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'
import { getLocalCardImageUrl } from '../utils/cardImages'

const MAN_EATER_BUG = '54652250'
const COLLAPSE_MS = 2000

const target = {
  name: 'settings',
  hash: '#feedback',
  query: { report: 'bug' },
} as const

const { t } = useI18n()
const router = useRouter()
const { menusCompact } = useCompactLayout()
const artUrl = computed(() => getLocalCardImageUrl(MAN_EATER_BUG))
const open = ref(false)
let collapseTimer: ReturnType<typeof setTimeout> | null = null

function clearCollapse(): void {
  if (!collapseTimer) return
  clearTimeout(collapseTimer)
  collapseTimer = null
}

function armCollapse(): void {
  clearCollapse()
  collapseTimer = setTimeout(() => {
    open.value = false
    collapseTimer = null
  }, COLLAPSE_MS)
}

function onTap(): void {
  if (!open.value) {
    open.value = true
    armCollapse()
    return
  }
  clearCollapse()
  void router.push(target)
}

watch(menusCompact, (compact) => {
  if (compact) return
  open.value = false
  clearCollapse()
})

onUnmounted(clearCollapse)
</script>

<style scoped>
.bug-report {
  position: fixed;
  left: max(16px, env(safe-area-inset-left, 0px));
  bottom: max(16px, env(safe-area-inset-bottom, 0px));
  z-index: 55;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 6px 18px 6px 6px;
  border: 0;
  border-radius: 28px;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  box-shadow: var(--md-elevation-2);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  line-height: 20px;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition:
    filter var(--md-motion-short) var(--md-motion-standard),
    transform var(--md-motion-short) var(--md-motion-standard),
    padding var(--md-motion-medium) var(--md-motion-standard),
    gap var(--md-motion-medium) var(--md-motion-standard);
}

.bug-report.collapsed {
  gap: 0;
  padding-right: 6px;
}

.bug-report:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.bug-report:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 3px;
}

.bug-label {
  display: grid;
  grid-template-columns: 1fr;
  min-width: 0;
  opacity: 1;
  transition:
    grid-template-columns var(--md-motion-medium) var(--md-motion-standard),
    opacity var(--md-motion-short) var(--md-motion-standard);
}

.bug-label > span {
  overflow: hidden;
  min-width: 0;
}

.bug-report.collapsed .bug-label {
  grid-template-columns: 0fr;
  opacity: 0;
}

/* Art window on the 813×1185 scan: x 98, y 216, 620px square. */
.bug-art {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  background: #14300c;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.35);
}

.bug-art img {
  position: absolute;
  width: 131.1%;
  max-width: none;
  height: auto;
  left: -15.6%;
  top: -34.8%;
}

@media (prefers-reduced-motion: reduce) {
  .bug-report,
  .bug-label {
    transition: none;
  }

  .bug-report:hover {
    transform: none;
  }
}
</style>
